import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import CommandCenter from "@/pages/CommandCenter";
import CandidatePool from "@/pages/CandidatePool";
import CandidateRecord from "@/pages/CandidateRecord";
import InterviewSessions from "@/pages/InterviewSessions";
import Communications from "@/pages/Communications";
import InterviewSetup from "@/pages/InterviewSetup";
import EmployerHandoff from "@/pages/EmployerHandoff";
import CandidateApply from "@/pages/CandidateApply";
import OnSiteApply from "@/pages/OnSiteApply";
import EmploymentConfirmation from "@/pages/EmploymentConfirmation";
import OnboardingPortal from "@/pages/OnboardingPortal";
import OnboardingAdmin from "@/pages/OnboardingAdmin";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import UsersAccess from "@/pages/UsersAccess";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { Toaster } from "@/components/ui/sonner";

function Protected({ children, owner }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="auth-screen"><div className="auth-card"><div className="auth-agency">MAKE IT HAPPEN PHILLY</div><p className="auth-sub">Loading…</p></div></div>;
  if (user === false) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (owner && user.role !== "owner_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      {/* External / scoped doorways — no internal login required */}
      <Route path="/handoff/:token" element={<EmployerHandoff />} />
      <Route path="/apply/:token" element={<CandidateApply />} />
      <Route path="/onsite/:token" element={<OnSiteApply />} />
      <Route path="/employment/:token" element={<EmploymentConfirmation />} />
      <Route path="/onboarding/:token" element={<OnboardingPortal />} />
      {/* Public interview registration is the site root (same as /apply) */}
      <Route path="/" element={<CandidateApply />} />
      {/* Clean public-facing aliases -> same canonical workflows (default employer token) */}
      <Route path="/apply" element={<CandidateApply />} />
      <Route path="/supervisor" element={<EmployerHandoff />} />
      <Route path="/onsite" element={<OnSiteApply />} />
      {/* Auth */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Internal dashboard */}
      <Route element={<Protected><AppShell user={user} /></Protected>}>
        <Route path="/dashboard" element={<CommandCenter />} />
        <Route path="/candidate-pool" element={<CandidatePool />} />
        <Route path="/candidates/:registrationId" element={<CandidateRecord />} />
        <Route path="/interview-sessions" element={<InterviewSessions />} />
        <Route path="/communications" element={<Communications />} />
        <Route path="/interview-setup" element={<InterviewSetup />} />
        <Route path="/onboarding-admin" element={<OnboardingAdmin />} />
        <Route path="/users-access" element={<Protected owner><UsersAccess /></Protected>} />
      </Route>
    </Routes>
  );
}

function App() {
  // Apply the saved / query Day-Night theme on every route, including the
  // unauthenticated Sign In / Forgot / Reset screens (before AppShell mounts).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("theme") || localStorage.getItem("mihp-theme");
    if (t === "night" || t === "day") document.documentElement.dataset.theme = t;
  }, []);
  return (
    <div className="mihp-app-root">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
